import * as React from 'react';
import { isNumber, isString, isUndefined } from 'lodash';

const intlNumberFormatter = new Intl.NumberFormat('en-US');

export const MAX_DECIMAL_FIXED = 2;

///////////
// TYPES //
///////////

export interface UseNumeralOptions {
  negative?: boolean;
  fixed?: number | boolean;
  maxValue?: string;
  minValue?: string;
}

//////////////////////
// HELPER FUNCTIONS //
//////////////////////

export function confineFormattedNumberToMinMaxRange(
  val: string,
  min: string = String(Number.MIN_SAFE_INTEGER),
  max: string = String(Number.MAX_SAFE_INTEGER)
) {
  // Looks pretty bad, but makes sure that any formatting isn't
  // lost by never returning a value thats been converted to a number
  const valNum = parseFloat(val);
  const minNum = parseFloat(min);
  const maxNum = parseFloat(max);

  // Check that all the values returned a number
  if (isNaN(valNum) || isNaN(minNum) || isNaN(maxNum)) {
    return '';
  }
  // Check to make sure min is lower than max
  if (minNum > maxNum) {
    return '';
  }
  if (valNum <= minNum) {
    return min;
  } else if (valNum >= maxNum) {
    return max;
  }
  return val;
}

export function fixDecimals(value: string, options: UseNumeralOptions = {}): string {
  if (options.fixed === false || (isNumber(options.fixed) && options.fixed < 1)) {
    return '';
  } else if (value === '') {
    return '.';
  }

  const fixed = !isUndefined(options.fixed) && options.fixed !== true ? options.fixed : MAX_DECIMAL_FIXED;
  const max = fixed + 1;
  let decimals = value !== undefined ? `.${value}` : '';

  if (decimals.length > max) {
    decimals = decimals.slice(0, max);
  }

  return decimals;
}

export function formatString(value: string, options: UseNumeralOptions = {}): string {
  if (value === '') {
    return '';
  } else if (value === '-') {
    return '-';
  }

  // Prevents from going from high values -> crazy values -> NaN -> ""
  const confinedValue = confineFormattedNumberToMinMaxRange(value, options.minValue, options.maxValue);

  const [left, right] = confinedValue.split('.');
  const formatted = intlNumberFormatter.format(Number.parseInt(left));
  const decimals = fixDecimals(right, options);

  return formatted + decimals;
}

export function parseString(value: string, options: UseNumeralOptions = {}): number | undefined {
  if (value === '' || value === '-') {
    return undefined;
  }

  // Prevents from going from high values -> crazy values -> NaN -> ""
  const confinedValue = confineFormattedNumberToMinMaxRange(value, options.minValue, options.maxValue);

  const [left, right] = confinedValue.split('.');
  const replaced = left.replace(/,/g, '');
  const decimals = fixDecimals(right, options);
  const result = parseFloat(replaced + decimals);

  return result;
}

export function sanitiseString(value: any, options: UseNumeralOptions = {}): string {
  if (!isString(value) && !isNumber(value)) {
    return '';
  }

  const negative = !isUndefined(options.negative) ? options.negative : false;
  const str = isNumber(value) ? String(value) : value;
  const minus = negative === true && str.charAt(0) === '-' ? '-' : '';

  return `${minus}${str.replace(/[^0-9.]/gi, '')}`;
}

////////////////////////
// MAIN HOOK FUNCTION //
////////////////////////

export function useNumeral(initialValue?: any, options?: UseNumeralOptions): any {
  const [formattedValue, setFormattedValue] = React.useState(
    formatString(sanitiseString(initialValue, options), options)
  );

  function setNumeralValue(value: any): number | undefined {
    const sanitisedValue = sanitiseString(value, options);
    const formattedNumber = formatString(sanitisedValue, options);
    const parsedString = parseString(sanitisedValue, options);

    setFormattedValue(formattedNumber);

    return parsedString;
  }

  React.useEffect(() => {
    setFormattedValue(formatString(sanitiseString(initialValue, options), options));
  }, [initialValue, options]);

  return [formattedValue, setNumeralValue];
}
